// Playbook d'une équipe (D-020, D-021) : les systèmes de jeu, dessinés et
// animés dans l'éditeur (page editor.html, moteur repris de Playbook BCL),
// avec leurs vidéos et images.
//  - staff : créer un play, ouvrir / ajouter une animation, ajouter une
//    vidéo, publier aux joueuses, supprimer ;
//  - joueuse / parent : les plays publiés, en lecture (vue joueuse animée).
// Rangé par catégorie, avec une recherche dès qu'il y a de quoi chercher.
import { createPlay, deletePlay, editorUrl, teamPlays, updatePlay, type Play, type PlaySide, type Team, type Video } from '../lib/api';
import { $, $$, confirmTwice, html, mount, openSheet, toast } from './dom';
import { imgSrc } from './play-images';

export const SIDE_LABEL: Record<PlaySide, string> = {
  attaque: 'Attaque', defense: 'Défense', transition: 'Transition', remise: 'Remise en jeu', special: 'Spécial', concept: 'Concept',
};
const SIDE_ORDER: PlaySide[] = ['attaque', 'defense', 'transition', 'remise', 'special', 'concept'];
const go = (url: string) => { location.href = url; };

/** Lien YouTube → identifiant de vidéo (pour la vignette), sinon null. */
export function youtubeId(url: string): string | null {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([A-Za-z0-9_-]{6,})/);
  return m ? m[1]! : null;
}

export const mediaBlock = (videos: Video[], images: string[]) => (videos.length || images.length) ? html`
  <div class="media">
    ${videos.map((v, i) => {
      const yt = youtubeId(v.url);
      return html`<a class="media-vid" href="${v.url}" target="_blank" rel="noopener">
        ${yt ? html`<img src="https://i.ytimg.com/vi/${yt}/mqdefault.jpg" alt="" loading="lazy">` : ''}
        <span>▶ Vidéo ${videos.length > 1 ? i + 1 : ''}</span></a>`;
    })}
    ${images.map(u => { const i = imgSrc(u); return i.aimg ? html`<a class="media-img"><img src="${i.src}" data-aimg="${i.aimg}" alt="Image du play"></a>`
      : html`<a class="media-img" href="${u}" target="_blank" rel="noopener"><img src="${u}" alt="Image du play" loading="lazy"></a>`; })}
  </div>` : '';

export async function renderPlaybook(el: HTMLElement, team: Team, staff: boolean): Promise<void> {
  const plays = await teamPlays(team.id);
  if (!staff && !plays.length) { mount(el, html``); return; }

  const row = (p: Play) => html`
    <li data-q="${`${p.title} ${p.tag ?? ''} ${SIDE_LABEL[p.side]}`.toLowerCase()}"><button type="button" class="pb-row" data-play="${p.id}">
      <span class="pb-dot side-${p.side}" aria-hidden="true"></span>
      <span class="pb-main"><strong>${p.title}</strong>
        <span class="muted small">${p.tag ? `${p.tag} · ` : ''}${p.animations.length
          ? `${p.animations.length} animation${p.animations.length > 1 ? 's' : ''}` : 'à dessiner'}${p.videos.length ? ' · vidéo' : ''}${p.images.length ? ' · images' : ''}</span></span>
      ${staff ? (p.published ? html`<span class="acc acc-mail">visible</span>` : html`<span class="acc acc-none">coachs</span>`) : ''}
      <span class="occ-go" aria-hidden="true">›</span>
    </button></li>`;

  const groups = SIDE_ORDER.map(s => ({ side: s, list: plays.filter(p => p.side === s) })).filter(g => g.list.length);

  mount(el, html`
    <section class="block">
      <div class="block-head">
        <h3>Playbook${plays.length ? html` <span class="muted small">${plays.length}</span>` : ''}</h3>
        ${staff ? html`<button type="button" class="btn btn-primary btn-sm" id="b-new-play">+ Play</button>` : ''}
      </div>
      ${plays.length > 8 ? html`<input class="search" type="search" id="pb-search" placeholder="Chercher un play (titre, repère)…" aria-label="Chercher un play">` : ''}
      ${plays.length ? groups.map(g => html`
        <details class="pb-group" ${groups.length <= 2 ? 'open' : ''}>
          <summary><span class="pb-dot side-${g.side}" aria-hidden="true"></span>${SIDE_LABEL[g.side]} <span class="muted small">${g.list.length}</span></summary>
          <ul class="pb-list">${g.list.map(row)}</ul>
        </details>`)
        : html`<p class="muted">Dessine les systèmes de l'équipe : positions, courses, écrans, passes, tirs, variantes — puis anime-les. L’équipe les revoit sur son téléphone.</p>`}
    </section>`);

  const reload = () => renderPlaybook(el, team, staff);

  const search = $<HTMLInputElement>(el, '#pb-search');
  search?.addEventListener('input', () => {
    const q = search.value.trim().toLowerCase();
    for (const d of $$<HTMLDetailsElement>(el, '.pb-group')) {
      let any = false;
      for (const li of $$(d, 'li')) { const hit = !q || li.dataset.q!.includes(q); li.hidden = !hit; any ||= hit; }
      d.hidden = !any;
      if (q) d.open = true;
    }
  });

  $(el, '#b-new-play')?.addEventListener('click', () => {
    openSheet('Nouveau play', html`
      <form class="form" id="f-play" novalidate>
        <label class="field"><span>Nom</span><input name="title" required maxlength="80" placeholder="ex. Attaque zone 2-3, Remise côté"></label>
        <div class="row-2">
          <label class="field"><span>Catégorie</span><select name="side">${SIDE_ORDER.map(s => html`<option value="${s}">${SIDE_LABEL[s]}</option>`)}</select></label>
          <label class="field"><span>Repère (facultatif)</span><input name="tag" maxlength="40" placeholder="ex. ZONE, BOX"></label>
        </div>
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Créer et dessiner</button>
      </form>`, (sheet) => {
      const f = $<HTMLFormElement>(sheet, '#f-play')!;
      f.addEventListener('submit', async (e) => {
        e.preventDefault();
        const d = new FormData(f);
        const title = String(d.get('title') ?? '').trim();
        if (!title) { $(f, '.form-msg')!.textContent = 'Donne un nom au play.'; return; }
        try {
          const p = await createPlay({ club_id: team.club_id, team_id: team.id, title, side: String(d.get('side')) as PlaySide, tag: String(d.get('tag') ?? '').trim() || null });
          go(editorUrl({ play: p.id }));
        } catch { $(f, '.form-msg')!.textContent = 'Création impossible. Réessaie.'; }
      });
    });
  });

  for (const b of $$<HTMLButtonElement>(el, '[data-play]')) {
    b.addEventListener('click', () => {
      const p = plays.find(x => x.id === b.dataset.play)!;
      // Joueuse : une seule animation et rien d'autre à montrer → directement en lecture.
      if (!staff && p.animations.length === 1 && !p.videos.length && !p.images.length && !p.description) {
        go(editorUrl({ play: p.id, anim: p.animations[0]!.id, view: true })); return;
      }
      openSheet(p.title, html`
        <p class="muted">${SIDE_LABEL[p.side]}${p.tag ? ` · ${p.tag}` : ''}</p>
        ${p.description ? html`<p class="pb-desc">${p.description}</p>` : ''}
        ${mediaBlock(p.videos, p.images)}
        ${p.animations.length ? html`<ul class="anim-list">${p.animations.map(a => html`<li>
          <div class="anim-row">
            <span class="pick-main"><strong>${a.title || p.title}</strong><span class="muted small">${Array.isArray(a.steps) ? a.steps.length : 0} temps · ${a.court_type === 'full' ? 'plein terrain' : a.court_type === 'training' ? 'atelier' : 'demi-terrain'}</span></span>
            <button type="button" class="btn btn-ghost btn-sm" data-view="${a.id}">Voir</button>
            ${staff ? html`<button type="button" class="btn btn-primary btn-sm" data-edit="${a.id}">Modifier</button>` : ''}
          </div></li>`)}</ul>` : html`<p class="muted">${staff ? 'Pas encore d\'animation.' : ''}</p>`}
        ${staff ? html`
          <div class="btn-row">
            <button type="button" class="btn btn-ghost" id="b-anim-new">+ Nouvelle animation</button>
          </div>
          <form class="inline-form" id="f-video" novalidate>
            <input name="url" type="url" inputmode="url" placeholder="Lien d'une vidéo (YouTube…)" aria-label="Lien vidéo">
            <button class="btn btn-ghost btn-sm" type="submit">Ajouter la vidéo</button>
          </form>
          <label class="check"><input type="checkbox" id="c-pub" ${p.published ? 'checked' : ''}>
            <span><strong>Visible de l’équipe</strong><br><span class="muted">Elle le revoit en lecture, animé, sur le téléphone.</span></span></label>
          <button type="button" class="btn btn-danger-ghost" id="b-play-del">Supprimer le play</button>` : ''}`, (sheet, close) => {
        for (const v of $$<HTMLButtonElement>(sheet, '[data-view]')) v.addEventListener('click', () => go(editorUrl({ play: p.id, anim: v.dataset.view!, view: true })));
        for (const v of $$<HTMLButtonElement>(sheet, '[data-edit]')) v.addEventListener('click', () => go(editorUrl({ play: p.id, anim: v.dataset.edit! })));
        $(sheet, '#b-anim-new')?.addEventListener('click', () => go(editorUrl({ play: p.id })));
        const fv = $<HTMLFormElement>(sheet, '#f-video');
        fv?.addEventListener('submit', async (e) => {
          e.preventDefault();
          const url = String(new FormData(fv).get('url') ?? '').trim();
          if (!/^https:\/\/\S+$/.test(url)) { toast('Colle un lien qui commence par https://', 'ko'); return; }
          try { await updatePlay(p.id, { videos: [...p.videos, { url, orientation: 'horizontal' }] }); close(); toast('Vidéo ajoutée'); await reload(); }
          catch { toast('Enregistrement impossible.', 'ko'); }
        });
        $<HTMLInputElement>(sheet, '#c-pub')?.addEventListener('change', async (e) => {
          const on = (e.target as HTMLInputElement).checked;
          try { await updatePlay(p.id, { published: on }); toast(on ? 'Visible de l’équipe' : 'Réservé aux coachs'); await reload(); }
          catch { toast('Enregistrement impossible.', 'ko'); }
        });
        const del = $<HTMLButtonElement>(sheet, '#b-play-del');
        if (del) confirmTwice(del, `Confirmer : supprimer « ${p.title} »${p.animations.length > 1 ? ` et ses ${p.animations.length} animations` : p.animations.length ? ' et son animation' : ''}`, async () => {
          try { await deletePlay(p.id); close(); toast('Play supprimé'); await reload(); }
          catch { toast('Suppression impossible.', 'ko'); }
        });
      });
    });
  }
}
